import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Order, OrderItem, OrdersState, OrderStatus } from '../../types/order';
import { CompletedProduct } from '../../types/todo';
import { calculateOrderFulfillment } from '../../utils/orderUtils';
import { checkOrderItemInventory } from '../../utils/orderFulfillmentUtils';
import { calculateOrderPricing } from '../../utils/pricingUtils';
import type { RootState } from '../store';
import type { Recipe } from '../../types/recipe';

const ORDERS_STORAGE_KEY = 'orders';
const COMPLETED_ORDERS_STORAGE_KEY = 'completed-orders';

/**
 * Loads orders from localStorage
 */
function loadOrdersFromStorage(): Order[] {
  console.log('[Orders] Loading orders from localStorage');

  try {
    const stored = localStorage.getItem(ORDERS_STORAGE_KEY);
    if (!stored) {
      console.log('[Orders] No data found');
      return [];
    }

    const parsed = JSON.parse(stored);

    if (!Array.isArray(parsed)) {
      console.warn('[Orders] Data is not an array');
      return [];
    }

    // Migrate old orders to add isFinalized, deliveryType, clientId, and customerType fields
    const migratedOrders = parsed.map((order: any) => ({
      ...order,
      isFinalized: order.isFinalized !== undefined ? order.isFinalized : true, // Assume old orders are finalized
      deliveryType: order.deliveryType || 'Delivery', // Default to 'Delivery' for old orders
      clientId: order.clientId || uuidv4(), // Generate a temporary clientId for old orders
      customerType: order.customerType || 'Individual' // Default to 'Individual' for old orders
    }));

    console.log(`[Orders] ✓ Loaded ${migratedOrders.length} orders`);
    return migratedOrders;
  } catch (error) {
    console.error('[Orders] Failed to load from storage:', error);
    return [];
  }
}

/**
 * Loads completed orders from localStorage
 */
function loadCompletedOrdersFromStorage(): Order[] {
  console.log('[Orders] Loading completed orders from localStorage');

  try {
    const stored = localStorage.getItem(COMPLETED_ORDERS_STORAGE_KEY);
    if (!stored) {
      console.log('[Orders] No completed orders found');
      return [];
    }

    const parsed = JSON.parse(stored);

    if (!Array.isArray(parsed)) {
      console.warn('[Orders] Completed orders data is not an array');
      return [];
    }

    console.log(`[Orders] ✓ Loaded ${parsed.length} completed orders`);
    return parsed;
  } catch (error) {
    console.error('[Orders] Failed to load completed orders from storage:', error);
    return [];
  }
}

// Initial state
const initialState: OrdersState = {
  orders: loadOrdersFromStorage(),
  completedOrders: loadCompletedOrdersFromStorage()
};

// Helper function to create a new order item
export function createOrderItem(recipeId: string = '', recipeName: string = '', quantity: number = 1): OrderItem {
  return {
    id: uuidv4(),
    recipeId,
    recipeName,
    quantity
  };
}

// Helper function to create a blank order
export function createBlankOrder(clientId: string): Order {
  const today = new Date().toISOString().split('T')[0];
  return {
    id: uuidv4(),
    customerName: '',
    customerType: 'Individual',
    email: '',
    phone: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    orderDate: today,
    deliveryType: 'Delivery',
    items: [],
    status: 'Pending',
    isFinalized: false,
    clientId
  };
}

/**
 * Thunk action to finalize an order with per-item inventory check and task generation
 *
 * This checks inventory for each item:
 * - Items WITH sufficient inventory: reserve the quantity
 * - Items WITHOUT sufficient inventory: create production tasks
 * - Mark order as "ready" ONLY if ALL items have sufficient inventory
 */
export const finalizeOrderWithInventoryCheck = createAsyncThunk<void, { orderId: string }, { state: RootState }>(
  'orders/finalizeOrderWithInventoryCheck',
  async ({ orderId }, { getState, dispatch }) => {
    const state = getState();
    const order = state.orders.orders.find(o => o.id === orderId);

    if (!order) {
      console.error(`[finalizeOrderWithInventoryCheck] Order ${orderId} not found`);
      return;
    }

    // Get all completed products (inventory)
    const completedProducts = state.todo.completedProducts;

    // Get all recipes (fillings)
    const recipes = state.recipes.fillings;

    // Calculate reserved inventory from other finalized orders
    const reservedInventory = new Map<string, number>();
    const sortedOrders = [...state.orders.orders]
      .filter(o => o.id !== orderId && o.isFinalized && o.status !== 'Completed')
      .sort((a, b) => new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime());

    sortedOrders.forEach(o => {
      // Use allocatedInventory instead of items to accurately reflect what was actually reserved
      if (o.allocatedInventory && o.allocatedInventory.length > 0) {
        o.allocatedInventory.forEach(allocated => {
          const current = reservedInventory.get(allocated.productName) || 0;
          reservedInventory.set(allocated.productName, current + allocated.quantity);
        });
      }
    });

    // Check inventory for each item
    const result = checkOrderItemInventory(order, completedProducts, reservedInventory, recipes);

    console.log(`[finalizeOrderWithInventoryCheck] Order ${order.customerName}:`);
    console.log(`  - Fully fulfilled: ${result.isFullyFulfilled}`);
    console.log(`  - Items with inventory: ${result.allocatedInventory.length}`);

    // Calculate pricing for the order
    const pricing = calculateOrderPricing(order, state.pricing);

    console.log(`[finalizeOrderWithInventoryCheck] Pricing calculated:`);
    console.log(`  - Subtotal: $${pricing.subtotal.toFixed(2)}`);
    console.log(`  - Delivery Fee: $${pricing.deliveryFee.toFixed(2)}`);
    console.log(`  - Total: $${pricing.total.toFixed(2)}`);

    // Warn if delivery order finalized without distance data
    if (order.deliveryType === 'Delivery' && !order.distanceFromHome && !pricing.deliveryZoneId) {
      console.warn(
        `[finalizeOrderWithInventoryCheck] Warning: Delivery order "${order.customerName}" ` +
          `finalized without distance data. Delivery fee may be $0. Geocoding likely still pending.`
      );
    }

    // Update the order with finalization status, ready status, allocated inventory, and pricing
    // AUTO-TRANSITION: If order has all items ready, move from Pending to In Progress
    const shouldAutoProgress = result.isFullyFulfilled && order.status === 'Pending';

    const updatedOrder: Order = {
      ...order,
      isFinalized: true,
      isReady: result.isFullyFulfilled,
      status: shouldAutoProgress ? 'In Progress' : order.status,
      allocatedInventory: result.allocatedInventory.length > 0 ? result.allocatedInventory : undefined,
      pricing
    };

    dispatch(updateOrder(updatedOrder));

    console.log(
      `[finalizeOrderWithInventoryCheck] ✓ Order finalized: ${updatedOrder.isReady ? 'READY' : 'Needs Production'}`
    );
    if (shouldAutoProgress) {
      console.log(
        `[finalizeOrderWithInventoryCheck] → Auto-transitioned order ${order.customerName} to 'In Progress' (all items ready)`
      );
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.push(action.payload);
    },

    updateOrder: (state, action: PayloadAction<Order>) => {
      const index = state.orders.findIndex(o => o.id === action.payload.id);
      if (index !== -1) {
        state.orders[index] = action.payload;
      }
    },

    deleteOrder: (state, action: PayloadAction<string>) => {
      state.orders = state.orders.filter(o => o.id !== action.payload);
    },

    updateOrderStatus: (state, action: PayloadAction<{ id: string; status: OrderStatus }>) => {
      const order = state.orders.find(o => o.id === action.payload.id);
      if (order) {
        order.status = action.payload.status;
        // Set completionDate when order is marked as Completed
        if (action.payload.status === 'Completed' && !order.completionDate) {
          order.completionDate = new Date().toISOString();
        }
      }
    },

    updateOrderCustomer: (state, action: PayloadAction<{ id: string; field: string; value: string }>) => {
      const order = state.orders.find(o => o.id === action.payload.id);
      if (order) {
        (order as any)[action.payload.field] = action.payload.value;
      }
    },

    addOrderItem: (state, action: PayloadAction<{ orderId: string; item: OrderItem }>) => {
      const order = state.orders.find(o => o.id === action.payload.orderId);
      if (order) {
        order.items.push(action.payload.item);
      }
    },

    updateOrderItem: (state, action: PayloadAction<{ orderId: string; item: OrderItem }>) => {
      const order = state.orders.find(o => o.id === action.payload.orderId);
      if (order) {
        const index = order.items.findIndex(i => i.id === action.payload.item.id);
        if (index !== -1) {
          order.items[index] = action.payload.item;
        }
      }
    },

    deleteOrderItem: (state, action: PayloadAction<{ orderId: string; itemId: string }>) => {
      const order = state.orders.find(o => o.id === action.payload.orderId);
      if (order) {
        order.items = order.items.filter(i => i.id !== action.payload.itemId);
      }
    },

    setOrderItems: (state, action: PayloadAction<{ orderId: string; items: OrderItem[] }>) => {
      const order = state.orders.find(o => o.id === action.payload.orderId);
      if (order) {
        order.items = action.payload.items;
      }
    },

    recalculateOrderFulfillment: (state, action: PayloadAction<CompletedProduct[]>) => {
      state.orders = calculateOrderFulfillment(state.orders, action.payload);
    },

    copyOrderToCompleted: (state, action: PayloadAction<string>) => {
      const order = state.orders.find(o => o.id === action.payload);
      if (order) {
        // Check if order is already in completed orders
        const alreadyCompleted = state.completedOrders.some(o => o.id === order.id);
        if (!alreadyCompleted) {
          // Create a copy of the order and add to completed orders
          state.completedOrders.push({ ...order });
          console.log(`[Orders] ✓ Copied order ${order.id} to completed orders`);
        }
      }
    },

    markOrderInventoryDeducted: (state, action: PayloadAction<string>) => {
      const order = state.orders.find(o => o.id === action.payload);
      if (order) {
        order.inventoryDeducted = true;
        console.log(`[Orders] ✓ Marked order ${order.id} as inventory deducted`);
      }
    },

    migrateCompletedOrders: state => {
      // Find orders marked as 'Completed' but not yet in completedOrders array
      const completedOrderIds = new Set(state.completedOrders.map(o => o.id));
      let migratedCount = 0;

      state.orders.forEach(order => {
        if (order.status === 'Completed' && !completedOrderIds.has(order.id)) {
          state.completedOrders.push({ ...order });
          console.log(`[Migration] ✓ Migrated completed order: ${order.customerName}`);
          migratedCount++;
        }
      });

      if (migratedCount > 0) {
        console.log(`[Migration] ✓ Migrated ${migratedCount} completed orders to completedOrders array`);
      }
    },

    deleteAllOrders: state => {
      state.orders = [];
    },

    deleteAllCompletedOrders: state => {
      state.completedOrders = [];
    }
  }
});

export const {
  addOrder,
  updateOrder,
  deleteOrder,
  deleteAllOrders,
  deleteAllCompletedOrders,
  updateOrderStatus,
  updateOrderCustomer,
  addOrderItem,
  updateOrderItem,
  deleteOrderItem,
  setOrderItems,
  recalculateOrderFulfillment,
  copyOrderToCompleted,
  markOrderInventoryDeducted,
  migrateCompletedOrders
} = ordersSlice.actions;

export default ordersSlice.reducer;
